import type { TerminalBalanceTransaction } from "../shared/balance";
import {
  enforceMaxRefundMinor,
  outstandingBalanceMinor,
  terminalTransactionForBalance,
} from "../shared/balance";
import type {
  DepositItem,
  PosAccountSnapshot,
  RefundItemRequest,
  RefundOperation,
} from "../shared/types";
import type { DepositApiClient } from "./deposit-api-client";
import type { LightspeedClient } from "./lightspeed-client";

export interface RefundOrchestratorOptions {
  pos: LightspeedClient;
  api: DepositApiClient;
  /** Immutable device catalog used to verify the POS mutation before payout. */
  depositItems: readonly Pick<DepositItem, "itemId" | "sku" | "valueMinor">[];
  paymentMethodId: string;
  maxRefundMinor: number;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onProgress?: (stage: RefundProgressStage) => void;
  onTerminalTransaction?: (transaction: TerminalBalanceTransaction) => void;
}

export type RefundProgressStage =
  | "preparing"
  | "booking_items"
  | "awaiting_terminal"
  | "terminal_accepted"
  | "finalizing_pos";

export type RefundOutcome =
  | {
      kind: "checkout";
      operation: RefundOperation;
      account: PosAccountSnapshot;
    }
  | {
      kind: "refunded";
      operation: RefundOperation;
      account: PosAccountSnapshot;
    }
  | { kind: "completed"; operation: RefundOperation }
  | { kind: "blocked"; operation?: RefundOperation; reason: string };

export class PosFinalizationError extends Error {
  constructor(
    readonly operation: RefundOperation,
    cause: unknown,
  ) {
    super(
      "Adyen refund succeeded but POS finalization failed; the refund will not be sent again",
      { cause },
    );
    this.name = "PosFinalizationError";
  }
}

export class ReturnVerificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReturnVerificationError";
  }
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export function itemQuantities(
  account: PosAccountSnapshot,
  itemIds: readonly string[],
  itemValuesMinor: Readonly<Record<string, number>> = {},
): Record<string, number> {
  const allowed = new Set(itemIds);
  const result: Record<string, number> = {};
  const amounts: Record<string, number> = {};
  for (const itemId of itemIds) result[itemId] = 0;
  for (const line of account.lines) {
    if (allowed.has(line.itemId)) {
      result[line.itemId] = (result[line.itemId] ?? 0) + line.quantity;
      amounts[line.itemId] =
        (amounts[line.itemId] ?? 0) + line.totalAmountMinor;
    }
  }
  for (const itemId of itemIds) {
    const valueMinor = itemValuesMinor[itemId];
    const amountMinor = Math.abs(amounts[itemId] ?? 0);
    if (
      Number.isSafeInteger(valueMinor) &&
      valueMinor > 0 &&
      amountMinor > 0 &&
      amountMinor % valueMinor === 0
    ) {
      result[itemId] = amountMinor / valueMinor;
    }
  }
  return result;
}

function hasSettledItemAmount(
  account: PosAccountSnapshot,
  itemId: string,
  valueMinor: number | undefined,
): boolean {
  if (
    typeof valueMinor !== "number" ||
    !Number.isSafeInteger(valueMinor) ||
    valueMinor <= 0
  )
    return false;
  const amountMinor = Math.abs(
    account.lines
      .filter((line) => line.itemId === itemId)
      .reduce((sum, line) => sum + line.totalAmountMinor, 0),
  );
  return amountMinor > 0 && amountMinor % valueMinor === 0;
}

export interface ReturnVerification {
  initialBalanceMinor: number;
  initialItemQuantities: Record<string, number>;
  requestedItems: RefundItemRequest[];
  expectedBalanceDeltaMinor: number;
  itemValuesMinor: Record<string, number>;
}

/** Poll the POS until it reports a genuine balance decrease caused by the return items. */
export async function pollForNegativeBalanceChange(
  pos: Pick<LightspeedClient, "getCurrentAccount">,
  previousBalanceMinor: number,
  options: Pick<
    RefundOrchestratorOptions,
    "pollIntervalMs" | "pollTimeoutMs" | "sleep"
  > &
    Partial<ReturnVerification> = {},
): Promise<PosAccountSnapshot> {
  const interval = options.pollIntervalMs ?? 250;
  const timeout = options.pollTimeoutMs ?? 5_000;
  const sleep = options.sleep ?? defaultSleep;
  const started = Date.now();
  let latest = await pos.getCurrentAccount();
  const verify = (current: PosAccountSnapshot): "ready" | "wait" => {
    if (
      options.expectedBalanceDeltaMinor === undefined ||
      !options.requestedItems ||
      !options.initialItemQuantities
    ) {
      return outstandingBalanceMinor(current) < previousBalanceMinor
        ? "ready"
        : "wait";
    }
    const actualDelta = previousBalanceMinor - outstandingBalanceMinor(current);
    if (actualDelta > options.expectedBalanceDeltaMinor) {
      throw new ReturnVerificationError(
        `POS balance changed by ${actualDelta}, expected exactly ${options.expectedBalanceDeltaMinor}`,
      );
    }
    let allExpectedItemsPresent = true;
    const currentQuantities = itemQuantities(
      current,
      Object.keys(options.initialItemQuantities),
      options.itemValuesMinor,
    );
    const requestedQuantities = new Map(
      options.requestedItems.map((item) => [item.itemId, item.quantity]),
    );
    const unsettledItemIds = new Set<string>();
    for (const [itemId, baselineQuantity] of Object.entries(
      options.initialItemQuantities,
    )) {
      const currentQuantity = currentQuantities[itemId] ?? 0;
      const maximumQuantity =
        baselineQuantity + (requestedQuantities.get(itemId) ?? 0);
      if (
        currentQuantity > maximumQuantity &&
        !hasSettledItemAmount(
          current,
          itemId,
          options.itemValuesMinor?.[itemId],
        )
      ) {
        // K-Series can briefly expose its scaled quantity before the matching
        // line amount is populated. Keep polling until the monetary value can
        // normalize that quantity instead of treating the transient snapshot
        // as a second booked unit.
        unsettledItemIds.add(itemId);
        allExpectedItemsPresent = false;
        continue;
      }
      if (currentQuantity > maximumQuantity)
        throw new ReturnVerificationError(
          `POS contains an unexpected added deposit unit for ${itemId}`,
        );
    }
    for (const requested of options.requestedItems) {
      if (unsettledItemIds.has(requested.itemId)) continue;
      const currentQuantity = currentQuantities[requested.itemId] ?? 0;
      const expectedQuantity =
        (options.initialItemQuantities[requested.itemId] ?? 0) +
        requested.quantity;
      if (currentQuantity > expectedQuantity)
        throw new ReturnVerificationError(
          `POS contains too many booked units for ${requested.itemId}`,
        );
      if (currentQuantity < expectedQuantity) allExpectedItemsPresent = false;
    }
    if (
      allExpectedItemsPresent &&
      actualDelta === options.expectedBalanceDeltaMinor
    )
      return "ready";
    return "wait";
  };
  while (verify(latest) !== "ready") {
    if (Date.now() - started >= timeout) {
      throw new Error(
        "POS did not confirm a negative account balance change after adding deposit items",
      );
    }
    await sleep(interval);
    latest = await pos.getCurrentAccount();
  }
  return latest;
}

/**
 * Owns the client-side portion of a return. The Worker owns durable journal and
 * Adyen idempotency. Before calling a non-idempotent POS finalization bridge,
 * this class persists `pos_finalizing`; an uncertain POS result is never
 * repeated automatically.
 */
export class RefundOrchestrator {
  constructor(private readonly options: RefundOrchestratorOptions) {}

  async start(
    requestedItems: RefundItemRequest[],
    idempotencyKey: string,
  ): Promise<RefundOutcome> {
    this.reportProgress("preparing");
    this.validateRequestedItems(requestedItems);
    const initial = await this.options.pos.getCurrentAccount();
    const existing = await this.options.api.findOpenOperation(
      initial.accountId,
    );

    // The durable operation owns this POS account until it reaches a terminal
    // state. A new button press must resume it, never create a second journal
    // with a fresh idempotency key.
    if (existing) return this.resume(existing, initial);

    // Existing configured deposit lines are part of the immutable baseline.
    // Only quantities added after this journal is created belong to this
    // operation, so an item already booked by Viator can safely be selected
    // again without being paid out twice.
    const itemIds = this.options.depositItems.map((item) => item.itemId);
    const itemValuesMinor = this.itemValuesMinor();
    const operation = await this.options.api.createOrResumeOperation({
      accountId: initial.accountId,
      idempotencyKey,
      requestedItems,
      initialBalanceMinor: outstandingBalanceMinor(initial),
      initialItemQuantities: itemQuantities(initial, itemIds, itemValuesMinor),
      expectedBalanceDeltaMinor: this.expectedBalanceDelta(requestedItems),
      currency: initial.currency,
    });
    return this.resume(operation, initial);
  }

  async resumeById(operationId: string): Promise<RefundOutcome> {
    this.reportProgress("preparing");
    const operation = await this.options.api.getOperation(operationId);
    return this.resume(operation);
  }

  async resume(
    operation: RefundOperation,
    knownAccount?: PosAccountSnapshot,
  ): Promise<RefundOutcome> {
    if (operation.status === "completed")
      return { kind: "completed", operation };
    if (
      operation.status === "blocked" ||
      operation.status === "failed" ||
      operation.status === "needs_reconciliation"
    ) {
      return {
        kind: "blocked",
        operation,
        reason:
          operation.failureReason ?? "Operation requires manual reconciliation",
      };
    }
    if (
      operation.status === "pos_finalizing" ||
      operation.status === "pos_finalization_failed"
    ) {
      return {
        kind: "blocked",
        operation,
        reason:
          "The POS finalization result is uncertain. Reconcile this operation in Lightspeed and Adyen before taking further action.",
      };
    }
    if (operation.status === "accepted") {
      if (
        !operation.adyenPspReference &&
        !operation.adyenTenderReference &&
        operation.refundAmountMinor !== undefined
      ) {
        // This re-enters only the Worker's status-query branch. The Worker has
        // already persisted that the original PaymentRequest was dispatched
        // and therefore cannot send a second refund.
        this.reportProgress("awaiting_terminal");
        await this.options.api.requestAdyenRefund(
          operation.id,
          operation.refundAmountMinor,
          operation.idempotencyKey,
          operation.adyenTransactionType ??
            (typeof operation.bookedBalanceMinor === "number" &&
            operation.bookedBalanceMinor > 0
              ? "payment"
              : "refund"),
        );
        operation = await this.options.api.getOperation(operation.id);
      }
      if (!operation.adyenPspReference && !operation.adyenTenderReference) {
        return {
          kind: "blocked",
          operation,
          reason:
            "The terminal accepted the refund but has not returned a transaction reference yet. Retry recovery later; no new refund will be sent.",
        };
      }
      this.reportProgress("terminal_accepted");
      return this.finalizePos(operation);
    }
    if (operation.status === "adyen_succeeded") {
      this.reportProgress("terminal_accepted");
      return this.finalizePos(operation);
    }
    if (operation.status === "awaiting_checkout") {
      const account =
        knownAccount ?? (await this.options.pos.getCurrentAccount());
      await this.options.pos.navigate("checkout");
      const completed = await this.options.api.updateOperation(operation.id, {
        status: "completed",
      });
      return { kind: "checkout", operation: completed, account };
    }

    const before = knownAccount ?? (await this.options.pos.getCurrentAccount());
    let account: PosAccountSnapshot;
    if (operation.status === "draft") {
      this.reportProgress("booking_items");
      let missingItems: RefundItemRequest[];
      try {
        missingItems = this.missingItems(operation, before);
      } catch (error) {
        return this.block(operation, error);
      }
      // One bridge call per unit is intentional: K-Series quantity handling
      // varies by version, while repeated single additions are deterministic.
      for (const line of missingItems) {
        for (let i = 0; i < line.quantity; i += 1)
          await this.options.pos.addItem(line.itemId, 1);
      }
      try {
        account = await pollForNegativeBalanceChange(
          this.options.pos,
          operation.initialBalanceMinor,
          {
            ...this.options,
            initialItemQuantities: operation.initialItemQuantities,
            requestedItems: operation.requestedItems,
            expectedBalanceDeltaMinor: operation.expectedBalanceDeltaMinor,
            itemValuesMinor: this.itemValuesMinor(),
          },
        );
      } catch (error) {
        return this.block(operation, error);
      }
      operation = await this.options.api.updateOperation(operation.id, {
        status: "items_booked",
        bookedBalanceMinor: outstandingBalanceMinor(account),
      });
    } else {
      account = before;
      // A crash after the journal patch is still verified against its original
      // snapshot. A changed POS account must never produce an inferred payout.
      try {
        this.assertExpectedReturn(operation, account);
      } catch (error) {
        return this.block(operation, error);
      }
    }

    const bookedBalanceMinor = outstandingBalanceMinor(account);
    const terminalTransaction =
      terminalTransactionForBalance(bookedBalanceMinor);
    if (!terminalTransaction) {
      this.reportProgress("finalizing_pos");
      operation = await this.options.api.updateOperation(operation.id, {
        status: "awaiting_checkout",
        bookedBalanceMinor,
      });
      await this.options.pos.navigate("checkout");
      operation = await this.options.api.updateOperation(operation.id, {
        status: "completed",
      });
      return { kind: "checkout", operation, account };
    }

    const transactionType = terminalTransaction.type;
    this.options.onTerminalTransaction?.(terminalTransaction);
    // Do not alter a previously journaled terminal amount on resume: its
    // Worker idempotency key is tied to that exact amount and direction.
    const amountMinor =
      transactionType === "refund"
        ? enforceMaxRefundMinor(
            operation.refundAmountMinor ?? terminalTransaction.amountMinor,
            this.options.maxRefundMinor,
          )
        : (operation.refundAmountMinor ?? terminalTransaction.amountMinor);
    operation = await this.options.api.updateOperation(operation.id, {
      status: "refund_pending",
      refundAmountMinor: amountMinor,
    });
    // Same key on a resume is safe because Worker must persist/request Adyen idempotently.
    this.reportProgress("awaiting_terminal");
    await this.options.api.requestAdyenRefund(
      operation.id,
      amountMinor,
      operation.idempotencyKey,
      transactionType,
    );
    // The Worker owns the accepted/approved decision and persists terminal
    // references before responding. Reload that durable state instead of
    // trying to reproduce its transition client-side.
    operation = await this.options.api.getOperation(operation.id);
    if (
      operation.status !== "accepted" &&
      operation.status !== "adyen_succeeded"
    ) {
      return {
        kind: "blocked",
        operation,
        reason:
          "Adyen did not leave the operation in an accepted settlement state.",
      };
    }
    this.reportProgress("terminal_accepted");
    return this.finalizePos(operation, account);
  }

  private async finalizePos(
    operation: RefundOperation,
    account?: PosAccountSnapshot,
  ): Promise<RefundOutcome> {
    this.reportProgress("finalizing_pos");
    const terminalReference =
      operation.adyenPspReference ?? operation.adyenTenderReference;
    if (!terminalReference)
      throw new Error(
        "Cannot finalize POS payment without an Adyen terminal reference",
      );
    const externalReference = `ADYEN:${terminalReference}`;
    // This checkpoint is deliberately before both bridge calls. If the web
    // extension crashes afterwards, automatic recovery stops and cannot pay
    // the account a second time.
    operation = await this.options.api.updateOperation(operation.id, {
      status: "pos_finalizing",
      externalReference,
    });
    try {
      await this.options.pos.addExternalReference(terminalReference, "ADYEN");
      const finalizedAccount = await this.options.pos.payAccount({
        paymentMethodId: this.options.paymentMethodId,
      });
      const completed = await this.options.api.updateOperation(operation.id, {
        status: "completed",
      });
      return {
        kind: "refunded",
        operation: completed,
        account:
          finalizedAccount ??
          account ??
          (await this.options.pos.getCurrentAccount()),
      };
    } catch (error) {
      const failed = await this.options.api.updateOperation(operation.id, {
        status: "pos_finalization_failed",
        failureReason: error instanceof Error ? error.message : String(error),
      });
      throw new PosFinalizationError(failed, error);
    }
  }

  private reportProgress(stage: RefundProgressStage): void {
    this.options.onProgress?.(stage);
  }

  private validateRequestedItems(items: RefundItemRequest[]): void {
    if (
      items.some(
        (line) =>
          !line.itemId ||
          !Number.isInteger(line.quantity) ||
          line.quantity <= 0,
      )
    ) {
      throw new Error("Deposit item quantities must be positive integers");
    }
    const allowed = new Set(
      this.options.depositItems.map((item) => item.itemId),
    );
    if (items.some((line) => !allowed.has(line.itemId)))
      throw new Error("A requested item is not configured as a deposit item");
  }

  private expectedBalanceDelta(items: RefundItemRequest[]): number {
    const values = new Map(
      this.options.depositItems.map((item) => [item.itemId, item.valueMinor]),
    );
    const total = items.reduce(
      (sum, item) => sum + (values.get(item.itemId) ?? 0) * item.quantity,
      0,
    );
    if (
      !Number.isSafeInteger(total) ||
      total < 0 ||
      (items.length > 0 && total === 0)
    )
      throw new Error(
        "Expected deposit value must be a non-negative integer amount",
      );
    return total;
  }

  private missingItems(
    operation: RefundOperation,
    current: PosAccountSnapshot,
  ): RefundItemRequest[] {
    if (
      !operation.initialItemQuantities ||
      !Number.isSafeInteger(operation.expectedBalanceDeltaMinor)
    ) {
      throw new ReturnVerificationError(
        "Refund journal lacks its initial POS item snapshot",
      );
    }
    const currentQuantities = itemQuantities(
      current,
      this.options.depositItems.map((item) => item.itemId),
      this.itemValuesMinor(),
    );
    return operation.requestedItems.flatMap((requested) => {
      const alreadyAdded =
        (currentQuantities[requested.itemId] ?? 0) -
        (operation.initialItemQuantities[requested.itemId] ?? 0);
      if (alreadyAdded > requested.quantity)
        throw new ReturnVerificationError(
          `POS has ${alreadyAdded} added units for ${requested.itemId}; expected at most ${requested.quantity}`,
        );
      if (alreadyAdded < 0)
        throw new ReturnVerificationError(
          `POS item quantity for ${requested.itemId} is below its journaled baseline`,
        );
      const missing = requested.quantity - alreadyAdded;
      return missing === 0
        ? []
        : [{ itemId: requested.itemId, quantity: missing }];
    });
  }

  private assertExpectedReturn(
    operation: RefundOperation,
    account: PosAccountSnapshot,
  ): void {
    const actualDelta =
      operation.initialBalanceMinor - outstandingBalanceMinor(account);
    if (actualDelta !== operation.expectedBalanceDeltaMinor) {
      throw new ReturnVerificationError(
        `POS balance delta is ${actualDelta}, expected ${operation.expectedBalanceDeltaMinor}`,
      );
    }
    const current = itemQuantities(
      account,
      this.options.depositItems.map((item) => item.itemId),
      this.itemValuesMinor(),
    );
    const requested = new Map(
      operation.requestedItems.map((item) => [item.itemId, item.quantity]),
    );
    for (const [itemId, baseline] of Object.entries(
      operation.initialItemQuantities,
    )) {
      const expected = baseline + (requested.get(itemId) ?? 0);
      if ((current[itemId] ?? 0) !== expected)
        throw new ReturnVerificationError(
          `POS quantity for ${itemId} differs from the journaled return`,
        );
    }
  }

  private async block(
    operation: RefundOperation,
    error: unknown,
  ): Promise<RefundOutcome> {
    const reason = error instanceof Error ? error.message : String(error);
    const blocked = await this.options.api.updateOperation(operation.id, {
      status: "blocked",
      failureReason: reason,
    });
    return { kind: "blocked", operation: blocked, reason };
  }

  private itemValuesMinor(): Record<string, number> {
    return Object.fromEntries(
      this.options.depositItems.map((item) => [item.itemId, item.valueMinor]),
    );
  }
}
